import { createGetChangeFieldName, formatHistoryChanges, getHistoryChanges } from 'components/core/History';
import type { CategoryHistoryData, CategoryHistoryEntryAction, CategoryHistorySnapshot } from './types';

/**
 * Both are maps with arbitrary keys, so a single edit produces one diff per key.
 * Category metadata nests one level deeper than the asset one
 * (additional.metadata.<key>.value and .inherited), hence 2 diffs per edited key.
 */
const MAX_DEPTH_PATHS = ['additional.images', 'additional.metadata'];

const FIELD_NAME_MAP = {
    parentId: 'parent category',
    showCategory: 'visibility',
    'additional.image': 'poster image',
    'additional.images': 'additional images',
} as const;

const IGNORED_FIELDS = [
    // history-consumer stamps `updated` on every record, so consecutive records always
    // differ in it - showing it would mean every entry reads "Updated time modified"
    'updated',
    // present on the creation record only, so diffing it against its successor
    // reports a bogus "Created time removed"
    'created',
    // changes whenever any asset is published into the category, which is not a category edit
    'lastAsset',
    'stats',
    'scrape',
    '_links',
    // internal flag toggled by SVP on image processing, not a user-facing edit
    'additional.isImageUploaded',
    // deprecated field, no longer surfaced anywhere
    'order',
    // immutable, so a diff against it is never meaningful
    'id',
    // deprecated, always empty
    'additional.shortDescription',
    // deprecated, no longer surfaced anywhere
    'additional.notificationMailEnabled',
];

export function isCategorySnapshot(entry: unknown): entry is CategoryHistorySnapshot {
    return (
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as CategoryHistorySnapshot).id === 'number' &&
        typeof (entry as CategoryHistorySnapshot).additional === 'object'
    );
}

/**
 * SVP serializes empty maps as arrays, and history-consumer stores the message as it comes.
 * Left alone, `[]` vs `{}` reports a change that never happened and the preview breaks
 * when it iterates metadata entries.
 */
export function normalizeSnapshot(snapshot: CategoryHistorySnapshot): CategoryHistorySnapshot {
    const additional = snapshot.additional ?? ({} as CategoryHistorySnapshot['additional']);

    return {
        ...snapshot,
        additional: {
            ...additional,
            // `CategoryImages` declares its known keys as `never`, so an empty map needs the cast
            images: (Array.isArray(additional.images)
                ? {}
                : (additional.images ?? {})) as CategoryHistorySnapshot['additional']['images'],
            metadata: Array.isArray(additional.metadata) ? {} : (additional.metadata ?? {}),
        },
    };
}

export function getEntryDate(snapshot: CategoryHistorySnapshot): number {
    return snapshot.updated ?? snapshot.created ?? 0;
}

export function getEntryAction(snapshot: CategoryHistorySnapshot): CategoryHistoryEntryAction {
    return snapshot.created ? 'created' : 'updated';
}

export function prepareCategoryHistoryData(rawHistoryData: unknown[] | undefined): CategoryHistoryData[] {
    if (!rawHistoryData) {
        return [];
    }

    return (
        rawHistoryData
            .filter(isCategorySnapshot)
            .map(normalizeSnapshot)
            .sort((a, b) => getEntryDate(b) - getEntryDate(a))
            .map((categoryData, index, list) => {
                // previous category is the next one on the list
                const previousCategoryData = list[index + 1] || categoryData;
                const allChanges = getHistoryChanges(previousCategoryData, categoryData);
                const date = getEntryDate(categoryData);

                return {
                    // timestamps are not unique enough to key rows on - records written within
                    // the same second would collide and share expansion state
                    id: `${index}:${date}`,
                    date,
                    action: getEntryAction(categoryData),
                    allChanges,
                    changes: formatHistoryChanges(allChanges, {
                        maxDepthPaths: MAX_DEPTH_PATHS,
                        ignoredFields: IGNORED_FIELDS,
                    }),
                    categoryData,
                };
            })
            // an entry with no visible changes is just diff noise (e.g. only ignored fields
            // differed) - except the oldest one, which has no earlier record to diff against
            // (self-diffed above) and must be kept regardless of its `action`: it may read
            // "updated" rather than "created" when history starts mid-lifecycle, e.g. the
            // very first edit after history tracking begins, or an older creation record
            // that has since aged out
            .filter((entry, index, entries) => entry.changes.length > 0 || index === entries.length - 1)
    );
}

export const getChangeFieldName = createGetChangeFieldName(FIELD_NAME_MAP);
